import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';

import { storeService } from '../../features/store-market/services/storeService';
import { orderService } from '../../features/store-market/services/orderService';
import { StoreFormData } from '../../features/store-market/types/store.types';
// import { showNotification } from '../../store/slices/notificationSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-tabpanel-${index}`}
      aria-labelledby={`store-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const StoreDashboardPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [store, setStore] = useState<StoreFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    activeProducts: 0,
    averageOrderValue: 0,
    conversionRate: 0,
  });

  useEffect(() => {
    if (storeId) {
      loadStoreData();
    }
  }, [storeId]);

  useEffect(() => {
    if (storeId && store) {
      loadStats();
    }
  }, [storeId, store]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await storeService.getStoreById(storeId!);
      setStore(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar dados da loja:', error);
      setError(error.message || 'Erro ao carregar dados da loja');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await orderService.getOrderStats(storeId!, 'month');
      // Mapear os dados da API para a interface esperada
      setStats({
        totalOrders: response.data.totalOrders || 0,
        totalRevenue: response.data.totalRevenue || 0,
        totalProducts: store?.products?.length || 0,
        activeProducts: store?.products?.filter(p => p.isActive !== false).length || 0,
        averageOrderValue: response.data.averageOrderValue || 0,
        conversionRate: response.data.conversionRate || 0,
      });
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditStore = () => {
    navigate(`/dashboard/lojas/${storeId}/editar`);
  };

  const handleViewStore = () => {
    if (store?.slug) {
      window.open(`/loja/${store.slug}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Carregando dashboard da loja...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !store) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || 'Loja não encontrada'}
        </Alert>
        <Button onClick={() => navigate('/dashboard/lojas')}>
          Voltar para Lista de Lojas
        </Button>
      </Container>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'paused':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'published':
        return 'Publicada';
      case 'draft':
        return 'Rascunho';
      case 'paused':
        return 'Pausada';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              {store.name}
            </Typography>
            <Chip
              label={getStatusLabel(store.status)}
              color={getStatusColor(store.status) as any}
              size="small"
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={handleViewStore}
            >
              Ver Loja
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditStore}
            >
              Editar Loja
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          {store.description}
        </Typography>
      </Box>

      {/* Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShoppingCartIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div" gutterBottom>
                {stats.totalOrders}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pedidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div" gutterBottom>
                R$ {stats.totalRevenue.toFixed(0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Receita
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <InventoryIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div" gutterBottom>
                {stats.totalProducts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Produtos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div" gutterBottom>
                R$ {stats.averageOrderValue.toFixed(0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ticket Médio
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" component="div" gutterBottom>
                {stats.conversionRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conversão
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Visão Geral" />
          <Tab label="Produtos" />
          <Tab label="Pedidos" />
          <Tab label="Configurações" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informações da Loja
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Nome:</strong> {store.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>URL:</strong> /loja/{store.slug}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Categoria:</strong> {store.category}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> {getStatusLabel(store.status)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Email:</strong> {store.contactEmail}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Telefone:</strong> {store.contactPhone}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ações Rápidas
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEditStore}
                  >
                    Editar Loja
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={handleViewStore}
                  >
                    Ver Loja Pública
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<InventoryIcon />}
                    onClick={() => setTabValue(1)}
                  >
                    Gerenciar Produtos
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Produtos da Loja ({store.products?.length || 0})
            </Typography>
            {store.products && store.products.length > 0 ? (
              <Grid container spacing={2}>
                {store.products.map((product, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {product.description}
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          R$ {product.price?.toFixed(2) || '0,00'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Estoque: {product.quantity || 0}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {product.featured && (
                            <Chip label="Destaque" size="small" color="primary" />
                          )}
                          {!product.isActive && (
                            <Chip label="Inativo" size="small" color="error" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  Nenhum produto adicionado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Adicione produtos para começar a vender
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pedidos Recentes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aqui você verá os pedidos da sua loja quando eles começarem a chegar.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configurações da Loja
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Métodos de Pagamento:</strong>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {store.paymentMethods?.map((method, index) => (
                    <Chip key={index} label={method} size="small" sx={{ mr: 1, mb: 1 }} />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valor Mínimo:</strong> R$ {store.minimumOrderValue?.toFixed(2) || '0,00'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Frete Grátis:</strong> R$ {store.freeShippingValue?.toFixed(2) || '0,00'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Parcelamento:</strong> {store.allowInstallments ? `Até ${store.maxInstallments}x` : 'Não permitido'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>
    </Container>
  );
};

export default StoreDashboardPage;
